import { EntityRepresentation } from '../DiffMergeTable/EntityRepresentation/EntityRepresentation';
import { last } from 'lodash';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import {
  APIComponentAttributes,
  APIEntityType,
  ArdoqId,
  isComponent,
} from '@ardoq/api-types';
import styled from 'styled-components';
import { EnhancedScopeDataWithBranchName } from './enhanceScopeData';
import { Graphics } from './types';
import { colors, s24 } from '@ardoq/design-tokens';
import { ENTITY_ID_ATTRIBUTE_KEY } from '../DiffMergeTable/EntityRepresentation/atoms';
import { getEntityById } from './utils/getEntityById';
import { ListItem, WithPopover } from '@ardoq/popovers';
import { ComponentLinkHandler } from '../DiffMergeTable/EntityRepresentation/ComponentRepresentationWithLink';
import { EnhancedScopeData } from '@ardoq/data-model';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { isTruthy } from '@ardoq/common-helpers';

const Wrapper = styled.div`
  display: inline-flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  max-height: ${s24};
  width: 100%;
`;

type EntitiesEllipsisProps = {
  entityIds: ArdoqId[];
  entityType: APIEntityType;
  parentEntityId?: ArdoqId;
  enhancedScopeData: EnhancedScopeDataWithBranchName | EnhancedScopeData;
  graphics: Graphics;
  overflowEntityId: ArdoqId | null;
  style?: CSSProperties;
  popoverWidth?: string;
  hideDefaultPopover?: boolean;
  getComponentLinkHandler?: (
    componentId: ArdoqId
  ) => ComponentLinkHandler | undefined;
  getComponentNavigationOptions?: (
    component: APIComponentAttributes
  ) => DropdownItem[];
  underlineOnHover?: boolean;
  variant?: 'default' | 'light';
};

const EntitiesList = ({
  entityIds,
  entityType,
  parentEntityId,
  enhancedScopeData,
  graphics,
  overflowEntityId,
  getComponentLinkHandler,
  getComponentNavigationOptions,
  underlineOnHover,
  variant,
}: EntitiesEllipsisProps) => {
  const overflowIndex = overflowEntityId
    ? entityIds.findIndex(entityId => overflowEntityId === entityId)
    : entityIds.length;
  return (
    <>
      {entityIds
        .map(entityId => ({
          entityId,
          entity: getEntityById(
            entityType,
            entityId,
            enhancedScopeData,
            parentEntityId
          ),
        }))
        .filter(
          ({ entity }, index) =>
            (overflowIndex === -1 || overflowIndex >= index) && isTruthy(entity)
        )
        .map(({ entityId, entity }, i) => {
          return (
            <EntityRepresentation
              entityId={entityId}
              entityType={entityType}
              parentEntityId={parentEntityId}
              enhancedScopeData={enhancedScopeData}
              graphics={graphics}
              isCommaSeparated={last(entityIds) !== entityId}
              hasLabelEllipsis={entityId === overflowEntityId}
              hideDefaultPopover
              key={i} // sometimes the same entity can appear multiple times in a list, so just using entityId is not enough for uniqueness
              componentLinkHandler={getComponentLinkHandler?.(entityId)}
              navigationOptions={
                isComponent(entity)
                  ? getComponentNavigationOptions?.(entity)
                  : undefined
              }
              underlineOnHover={underlineOnHover}
              variant={variant}
              linkColor={variant === 'light' ? colors.textModerate : undefined}
            />
          );
        })}
    </>
  );
};

// Enable ellipsis earlier to avoid case when we display icon only
const TRIM_MARGIN = 32;
const findOverflowedEntityId = (
  elements: Element[],
  wrapperRect: DOMRect,
  maxLength: number
) => {
  let entityId: ArdoqId | null = null;
  const maxWidth = Math.min(
    maxLength + wrapperRect.x,
    wrapperRect.width + wrapperRect.x
  );
  const hasOverFlow = elements.some(element => {
    if (element.getAttribute(ENTITY_ID_ATTRIBUTE_KEY)) {
      entityId = element.getAttribute(ENTITY_ID_ATTRIBUTE_KEY);
    }
    const { width, x } = element.getBoundingClientRect();
    return x + width + TRIM_MARGIN > maxWidth;
  });
  return hasOverFlow ? entityId : null;
};

const hasOwnChildren = (element: Element): boolean =>
  Boolean(element.children.length) &&
  Array.from(element.children).some(kid =>
    Boolean(kid.getAttribute(ENTITY_ID_ATTRIBUTE_KEY))
  );

const useFindOverflowElementEntityId = (maxLength: number) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [overflowEntityId, setOverflowEntityId] = useState<ArdoqId | null>(
    null
  );
  useEffect(() => {
    if (wrapperRef.current) {
      const directChildren = Array.from(wrapperRef.current.children);
      const elements = directChildren.flatMap(child =>
        hasOwnChildren(child) ? Array.from(child.children) : child
      );
      setOverflowEntityId(
        findOverflowedEntityId(
          elements,
          wrapperRef.current.getBoundingClientRect(),
          maxLength
        )
      );
    }
  }, [wrapperRef, maxLength]);

  return { wrapperRef, overflowEntityId };
};

const EntitiesListPopoverWrapper = styled.div`
  min-width: 150px;
  display: flex;
  flex-direction: column;
`;

export const EntitiesListPopoverContent = ({
  popoverWidth,
  entityIds,
  entityType,
  parentEntityId,
  enhancedScopeData,
  graphics,
  hideDefaultPopover,
  getComponentLinkHandler,
}: Omit<EntitiesEllipsisProps, 'overflowEntityId'>) => (
  <EntitiesListPopoverWrapper style={{ width: popoverWidth }}>
    {entityIds.map(id => (
      <ListItem key={id}>
        <EntityRepresentation
          entityId={id}
          entityType={entityType}
          parentEntityId={parentEntityId}
          enhancedScopeData={enhancedScopeData}
          graphics={graphics}
          componentLinkHandler={getComponentLinkHandler?.(id)}
          hideDefaultPopover={
            hideDefaultPopover || Boolean(getComponentLinkHandler)
          }
        />
      </ListItem>
    ))}
  </EntitiesListPopoverWrapper>
);

export const EntitiesEllipsis = ({
  style = {},
  maxLength,
  popoverWidth = 'auto',
  entityIds,
  entityType,
  parentEntityId,
  graphics,
  enhancedScopeData,
  getComponentLinkHandler,
  getComponentNavigationOptions,
  hideDefaultPopover,
  ...props
}: Omit<EntitiesEllipsisProps, 'overflowEntityId'> & { maxLength: number }) => {
  const { wrapperRef, overflowEntityId } =
    useFindOverflowElementEntityId(maxLength);

  return (
    <WithPopover
      content={
        overflowEntityId !== null
          ? () => (
              <EntitiesListPopoverContent
                entityIds={entityIds}
                entityType={entityType}
                parentEntityId={parentEntityId}
                enhancedScopeData={enhancedScopeData}
                graphics={graphics}
                popoverWidth={popoverWidth}
                hideDefaultPopover={hideDefaultPopover}
                getComponentLinkHandler={getComponentLinkHandler}
              />
            )
          : null
      }
    >
      <Wrapper ref={wrapperRef} style={{ ...style, maxWidth: maxLength }}>
        <EntitiesList
          entityType={entityType}
          entityIds={entityIds}
          parentEntityId={parentEntityId}
          graphics={graphics}
          enhancedScopeData={enhancedScopeData}
          overflowEntityId={overflowEntityId}
          getComponentLinkHandler={getComponentLinkHandler}
          getComponentNavigationOptions={getComponentNavigationOptions}
          {...props}
        />
      </Wrapper>
    </WithPopover>
  );
};
